import { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SectionHeader from "../SectionHeader";
import PlaylistRow from "./PlaylistRow";
import styles from "./styles";
import { getYouTubeId } from "../../utils/embed";
import { useYouTubeTitle } from "../../hooks/useYouTubeTitle";

const YouTubeWidgets = ({ youtubeLinks, description = "Sets and streams from this artist" }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);

  const validLinks = useMemo(
    () => (youtubeLinks || []).filter(getYouTubeId),
    [youtubeLinks],
  );

  const { data: activeTitle } = useYouTubeTitle(validLinks[activeIndex]);

  const handleSelect = (i) => {
    setActiveIndex(i);
    setHasInteracted(true);
  };

  const goPrev = () => handleSelect((activeIndex - 1 + validLinks.length) % validLinks.length);

  const goNext = useCallback(() => {
    if (validLinks.length <= 1) return;
    setActiveIndex((i) => (i + 1) % validLinks.length);
    setHasInteracted(true);
  }, [validLinks.length]);

  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== "https://www.youtube.com") return;
      try {
        const data = JSON.parse(e.data);
        if (data.event === "onStateChange" && data.info === 0) goNext();
      } catch {}
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [goNext]);

  if (!validLinks.length) return null;

  const activeId = getYouTubeId(validLinks[activeIndex]);
  const hasMultiple = validLinks.length > 1;

  return (
    <Box sx={styles.wrapperSx}>
      <SectionHeader title="Videos" description={description} />

      <Box>
        <Box sx={styles.cardSx}>
          <Box sx={styles.playerBoxSx}>
            <iframe
              key={`${activeIndex}-${hasInteracted}`}
              title={`YouTube Video ${activeIndex + 1}`}
              src={`https://www.youtube.com/embed/${activeId}?rel=0&modestbranding=1&iv_load_policy=3&enablejsapi=1${hasInteracted ? "&autoplay=1" : ""}`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={styles.iframeStyle}
            />
          </Box>

          <Box sx={styles.navRowSx(hasMultiple)}>
            <Typography sx={styles.navTitleSx}>{activeTitle || ""}</Typography>

            {hasMultiple && (
              <Box sx={styles.navControlsSx}>
                <IconButton onClick={goPrev} size="small" sx={styles.navBtnSx}>
                  <ChevronLeftIcon sx={styles.navIconSx} />
                </IconButton>
                <Typography sx={styles.counterSx}>
                  {activeIndex + 1}&thinsp;/&thinsp;{validLinks.length}
                </Typography>
                <IconButton onClick={goNext} size="small" sx={styles.navBtnSx}>
                  <ChevronRightIcon sx={styles.navIconSx} />
                </IconButton>
              </Box>
            )}
          </Box>

          {hasMultiple && (
            <Box>
              {validLinks.map((url, i) => (
                <PlaylistRow
                  key={url}
                  url={url}
                  index={i}
                  isActive={i === activeIndex}
                  onClick={() => handleSelect(i)}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default YouTubeWidgets;
